// MealPlanContext.js - Enhanced with Streaming Support
import React, { createContext, useContext, useState, useCallback } from 'react';

const MealPlanContext = createContext();

export const useMealPlan = () => useContext(MealPlanContext);

export const MealPlanProvider = ({ children }) => {
  const [mealPlan, setMealPlan] = useState([]);
  const [totalCost, setTotalCost] = useState('');
  const [productList, setProductList] = useState([]);
  const [maxStorageTime, setMaxStorageTime] = useState(5);
  const [budget, setBudget] = useState(null); // Add budget state
  const [leftovers, setLeftovers] = useState([]); // Leftovers state
  
  // "I have it" functionality state
  const [haveItState, setHaveItState] = useState({});
  const [adjustedTotalCost, setAdjustedTotalCost] = useState(null);
  
  // Shopping mode state (for saved meals)
  const [shoppingModeState, setShoppingModeState] = useState({});

  // User-added "My products" list (raw product documents added in the grocery list)
  const [myProducts, setMyProducts] = useState([]);
  
  // Streaming status
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamProgress, setStreamProgress] = useState({
    recipes: 0,
    products: 0,
    totalRecipes: 9
  });

  // Enhanced product list setter that handles partial updates
  const updateProductList = useCallback((newProducts) => {
    if (!newProducts || !Array.isArray(newProducts) || newProducts.length === 0) {
      return;
    }

    setProductList(currentProducts => {
      // If we don't have products yet, just use the new ones
      if (!currentProducts || currentProducts.length === 0) {
        return newProducts;
      }

      // Otherwise, merge the products by category
      const mergedProducts = [...currentProducts];
      const categoryMap = new Map(mergedProducts.map((cat, index) => [cat.category, index]));

      newProducts.forEach(category => {
        if (categoryMap.has(category.category)) {
          // This category exists - merge items
          const existingCategoryIndex = categoryMap.get(category.category);
          const existingItems = mergedProducts[existingCategoryIndex].items;
          const existingItemNames = new Set(existingItems.map(item => item.productName));

          // Add only new items
          category.items.forEach(item => {
            if (!existingItemNames.has(item.productName)) {
              existingItems.push(item);
            }
          });
        } else {
          // New category - add it
          mergedProducts.push(category);
          categoryMap.set(category.category, mergedProducts.length - 1);
        }
      });

      return mergedProducts;
    });
  }, []);

  // Start streaming process
  const startStreaming = useCallback(() => {
    setIsStreaming(true);
    setStreamProgress({
      recipes: 0,
      products: 0,
      totalRecipes: 9
    });
  }, []);

  // Update stream progress
  const updateStreamProgress = useCallback((progress) => {
    setStreamProgress(current => ({
      ...current,
      ...progress
    }));
  }, []);

  // End streaming process
  const endStreaming = useCallback(() => {
    setIsStreaming(false);
  }, []);

  // Helper function to update "I have it" state and recalculate adjusted total
  const updateHaveItState = useCallback((productName, hasIt, productList, getCurrencyInfo) => {
    setHaveItState(prev => {
      const newState = { ...prev, [productName]: hasIt };
      
      // Calculate new adjusted total after state change
      const newAdjustedTotal = productList.reduce((sum, cat) => {
        return sum + cat.items.reduce((c, item) => {
          if (item.unmatched === true || item.error) {
            return c;
          }
          if (newState[item.productName]) {
            return c; // Skip items user has
          }
          const num = parseFloat((item.totalPrice || '').toString().replace(/[^0-9.-]/g, ''));
          return c + (isNaN(num) ? 0 : num);
        }, 0);
      }, 0);
      
      // Format the adjusted total
      const currencyInfo = getCurrencyInfo ? getCurrencyInfo() : { symbol: '€' };
      const formattedTotal = `${currencyInfo.symbol}${(isNaN(newAdjustedTotal) ? 0 : newAdjustedTotal).toFixed(2)}`;
      setAdjustedTotalCost(formattedTotal);
      
      return newState;
    });
  }, []);

  // Reset the entire meal plan state
  const resetMealPlan = useCallback(async () => {
    // Clear the alternatives cache when resetting meal plan
    try {
      const { clearAlternativesCache } = await import('../components/MealPlan/AlternativeRecipes');
      clearAlternativesCache();
    } catch (error) {
      console.error('Error clearing alternatives cache:', error);
    }
    
    setMealPlan([]);
    setTotalCost('');
    setProductList([]);
    setMaxStorageTime(5);
    setBudget(null); // Reset budget as well
    setLeftovers([]); // Reset leftovers
    setHaveItState({}); // Reset "I have it" state
    setShoppingModeState({}); // Reset shopping mode state
    setMyProducts([]); // Reset user-added products
    setAdjustedTotalCost(null); // Reset adjusted total
    setIsStreaming(false);
    setStreamProgress({
      recipes: 0,
      products: 0,
      totalRecipes: 9
    });
  }, []);

  const value = {
    mealPlan,
    setMealPlan,
    totalCost,
    setTotalCost,
    productList,
    setProductList,
    updateProductList,
    maxStorageTime,
    setMaxStorageTime,
    budget,
    setBudget,
    leftovers,
    setLeftovers,
    haveItState,
    setHaveItState,
    shoppingModeState,
    setShoppingModeState,
    myProducts,
    setMyProducts,
    adjustedTotalCost,
    setAdjustedTotalCost,
    updateHaveItState,
    isStreaming,
    streamProgress,
    startStreaming,
    updateStreamProgress,
    endStreaming,
    resetMealPlan
  };

  return (
    <MealPlanContext.Provider value={value}>
      {children}
    </MealPlanContext.Provider>
  );
};